import React, { useEffect, useMemo, useState, useCallback } from "react";
import useMeasure from "react-use-measure";
import { useTransition, animated } from "@react-spring/web";
import { Link } from "react-router-dom";
import "./new-arrivals-grid.css";
import { encodeId } from "../../../utils/common";

// Column count tied to viewport width
function useColumns() {
  const getColumns = () => {
    if (typeof window === "undefined") return 2;
    const w = window.innerWidth;
    if (w >= 1500) return 5;
    if (w >= 1000) return 4;
    if (w >= 600) return 3;
    return 2;
  };
  const [columns, setColumns] = useState(getColumns);
  useEffect(() => {
    const handler = () => setColumns(getColumns());
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return columns;
}

// Fisher-Yates shuffle — no extra dependency needed
function shuffleArray(arr) {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

const New_arrivals_grid = ({ products = [], shuffleInterval = 3000 }) => {
  const columns = useColumns();
  const [measureRef, { width }] = useMeasure();

  const [ratios, setRatios] = useState({});
  const [order, setOrder] = useState(products);

  // Keep local order in sync whenever the real product list changes (new fetch, etc.)
  useEffect(() => {
    setOrder(products);
  }, [products]);

  // Periodically reshuffle -> triggers the animated reflow, same as the reference demo
  useEffect(() => {
    if (!shuffleInterval) return;
    const t = setInterval(() => {
      setOrder((current) => shuffleArray(current));
    }, shuffleInterval);
    return () => clearInterval(t);
  }, [shuffleInterval]);

  const handleImageLoad = useCallback((id, e) => {
    const { naturalWidth, naturalHeight } = e.target;
    if (!naturalWidth || !naturalHeight) return;
    setRatios((prev) =>
      prev[id] ? prev : { ...prev, [id]: naturalHeight / naturalWidth }
    );
  }, []);

  const columnWidth = columns > 0 ? width / columns : 0;

  const [colHeights, gridItems] = useMemo(() => {
    const heights = new Array(columns).fill(0);
    const items = order.map((product) => {
      const ratio = ratios[product.id] || 0.75;
      const itemHeight = columnWidth * ratio;
      const column = heights.indexOf(Math.min(...heights));
      const x = columnWidth * column;
      const y = heights[column];
      heights[column] += itemHeight;
      return {
        ...product,
        x,
        y,
        width: columnWidth,
        height: itemHeight,
      };
    });
    return [heights, items];
  }, [order, columns, columnWidth, ratios]);

  const transitions = useTransition(gridItems, {
    key: (item) => item.id,
    from: ({ x, y, width, height }) => ({ x, y, width, height, opacity: 0 }),
    enter: ({ x, y, width, height }) => ({ x, y, width, height, opacity: 1 }),
    update: ({ x, y, width, height }) => ({ x, y, width, height }),
    leave: { height: 0, opacity: 0 },
    config: { mass: 5, tension: 500, friction: 100 },
    trail: 25,
  });

  return (
    <div
      ref={measureRef}
      className="new_arrivals_masonry"
      style={{ height: Math.max(...colHeights, 0) }}
    >
      {transitions((style, item) => (
        <animated.div className="masonry_item" style={style}>
          <Link
            to={`/product-descrption/${encodeId(item?.id)}`}
            className="masonry_item_link"
          >
            <img
              className="masonry_item_img"
              src={process.env.REACT_APP_IMAGE_URL + item.image}
              alt={item.name || "Eyewear frame"}
              onLoad={(e) => handleImageLoad(item.id, e)}
            />
          </Link>
        </animated.div>
      ))}
    </div>
  );
};

export default New_arrivals_grid;